import type { LngLat } from "../adapters/types";

const METERS_PER_DEGREE = 111_320;

/**
 * Un mur, en ligne brisée (un contour fermé répète son premier point). `side` impose le côté où se
 * pose l'objet, à gauche ou à droite dans le sens de parcours : l'intérieur d'une zone creusée.
 * Sans lui, l'objet se pose du côté d'où l'on s'approche.
 */
export interface Wall {
  points: LngLat[];
  side?: "left" | "right";
}

export interface WallSnap {
  position: LngLat;
  /** Cap vers lequel regarde l'objet collé : à l'opposé du mur, vers son côté. */
  rotationDeg: number;
}

/**
 * Colle un point au mur le plus proche, à moins de `maxMeters` : il glisse sur le mur, décalé de
 * `offsetMeters` vers son côté, et regarde dans ce sens.
 */
export function snapToWall(point: LngLat, walls: Wall[], maxMeters: number, offsetMeters = 0): WallSnap | null {
  const metersPerLng = METERS_PER_DEGREE * Math.cos((point[1] * Math.PI) / 180);
  const local = ([lng, lat]: LngLat): [number, number] => [(lng - point[0]) * metersPerLng, (lat - point[1]) * METERS_PER_DEGREE];

  let best: { distance: number; foot: [number, number]; normal: [number, number] } | null = null;
  for (const { points, side } of walls) {
    for (let i = 0; i < points.length - 1; i++) {
      const [ax, ay] = local(points[i]!);
      const [bx, by] = local(points[i + 1]!);
      const dx = bx - ax;
      const dy = by - ay;
      const lengthSq = dx * dx + dy * dy;
      if (!lengthSq) continue;
      const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSq));
      const foot: [number, number] = [ax + dx * t, ay + dy * t];
      const distance = Math.hypot(foot[0], foot[1]);
      if (distance > maxMeters || (best && distance >= best.distance)) continue;
      const length = Math.sqrt(lengthSq);
      let normal: [number, number] = [-dy / length, dx / length];
      if (side === "right" || (!side && normal[0] * -foot[0] + normal[1] * -foot[1] < 0)) normal = [-normal[0], -normal[1]];
      best = { distance, foot, normal };
    }
  }
  if (!best) return null;

  const [x, y] = [best.foot[0] + best.normal[0] * offsetMeters, best.foot[1] + best.normal[1] * offsetMeters];
  const rotationDeg = ((Math.atan2(best.normal[0], best.normal[1]) * 180) / Math.PI + 360) % 360;
  return { position: [point[0] + x / metersPerLng, point[1] + y / METERS_PER_DEGREE], rotationDeg };
}

/**
 * Le point où le segment de `from` à `to` traverse l'un de ces contours, le plus proche de `from` ;
 * `null` s'il n'en traverse aucun.
 */
export function firstCrossing(from: LngLat, to: LngLat, rings: LngLat[][]): LngLat | null {
  const metersPerLng = METERS_PER_DEGREE * Math.cos((from[1] * Math.PI) / 180);
  const local = ([lng, lat]: LngLat): [number, number] => [(lng - from[0]) * metersPerLng, (lat - from[1]) * METERS_PER_DEGREE];
  const [tx, ty] = local(to);

  let best: number | null = null;
  for (const ring of rings) {
    for (let i = 0; i < ring.length - 1; i++) {
      const [ax, ay] = local(ring[i]!);
      const [bx, by] = local(ring[i + 1]!);
      const ex = bx - ax;
      const ey = by - ay;
      const denominator = tx * ey - ty * ex;
      if (!denominator) continue;
      const along = (ax * ey - ay * ex) / denominator;
      const across = (ax * ty - ay * tx) / denominator;
      if (along < 0 || along > 1 || across < 0 || across > 1) continue;
      if (best === null || along < best) best = along;
    }
  }
  if (best === null) return null;
  return [from[0] + (to[0] - from[0]) * best, from[1] + (to[1] - from[1]) * best];
}
